import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import type { Truck, Warehouse } from '../types/admin';

interface ListResponse<T> {
  success: boolean;
  data: T[];
}

interface TruckResponse {
  success: boolean;
  data: Truck;
}

export function useFleet(orgId: string | null) {
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api.get<ListResponse<Truck>>(`/fleet/${orgId}/trucks`),
      api.get<ListResponse<Warehouse>>(`/fleet/${orgId}/warehouses`),
    ])
      .then(([trucksRes, warehousesRes]) => {
        if (!cancelled) {
          setTrucks(trucksRes.data);
          setWarehouses(warehousesRes.data);
          setLoading(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load fleet data');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [orgId]);

  const updateTruckLocation = useCallback(
    async (truckId: string, currentLocation: string) => {
      if (!orgId) return;
      const res = await api.patch<TruckResponse>(`/fleet/${orgId}/trucks/${truckId}/location`, { currentLocation });
      setTrucks(prev => prev.map(t => t.id === truckId ? res.data : t));
      return res.data;
    },
    [orgId],
  );

  return { trucks, warehouses, loading, error, updateTruckLocation };
}
